import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';

import { activityAPI } from '../../services/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const UserActivityPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await activityAPI.user(id);
        setData(res.data);
      } catch (e) {
        toast({ variant: 'destructive', title: 'Failed to load user activity', description: String(e) });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleRefresh = async () => {
    try {
      setLoading(true);
      const res = await activityAPI.user(id);
      setData(res.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Failed to load user activity', description: String(e) });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-slate-500">Loading activity...</div>;
  if (!data) return <div className="text-slate-500">No data</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">User Activity</h1>
          <p className="text-slate-500 dark:text-slate-400">{data.user?.name} • {data.user?.email}</p>
        </div>
        <Button variant="outline" onClick={handleRefresh}>Refresh</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Sessions</CardTitle>
          <CardDescription>Active sessions + recent session records</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge className={data.sessions?.active ? 'bg-emerald-600' : 'bg-slate-500'}>
              Active: {data.sessions?.active ? 'YES' : 'NO'}
            </Badge>
            <span className="text-sm text-slate-600">Count: {data.sessions?.active_count ?? 0}</span>
          </div>

          <div className="space-y-2">
            {(data.sessions?.rows || []).slice(0, 20).map((s) => (
              <div key={s.id} className="p-3 rounded border bg-white dark:bg-slate-900">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{s.id} • {s.auth_method}</p>
                  <p className="text-xs text-slate-500">{s.created_at}</p>
                </div>
                <p className="text-xs text-slate-500">IP: {s.ip_address || '-'} • Expires: {s.expires_at || '-'}</p>
                {s.user_agent ? <p className="text-xs text-slate-500 break-words mt-1">{s.user_agent}</p> : null}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Login history</CardTitle>
          <CardDescription>Most recent attempts with IP enrichment</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(data.login_history || []).slice(0, 50).map((e) => (
            <div key={e.id} className="p-3 rounded border bg-white dark:bg-slate-900">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{e.auth_method} • {e.success ? 'SUCCESS' : 'FAIL'}</p>
                <p className="text-xs text-slate-500">{e.created_at}</p>
              </div>
              <p className="text-xs text-slate-500">IP: {e.ip_address || '-'} {e.sso_provider ? `• Provider: ${e.sso_provider}` : ''}</p>
              {e.location?.country ? (
                <p className="text-xs text-slate-600 mt-1">
                  Location: {e.location.country} ({e.location.country_code}) • ASN: {e.location.as_name}
                </p>
              ) : null}
              {!e.success && e.failure_reason ? <p className="text-xs text-red-600 mt-1">{e.failure_reason}</p> : null}
              {e.user_agent ? <p className="text-xs text-slate-500 break-words mt-1">{e.user_agent}</p> : null}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserActivityPage;
